import React, { useState } from "react";
import { Card, Box, Button } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { Order, useCancelOrderMutation } from "features/trade/tradeApi";
import { LoadingButton } from "@mui/lab";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

interface OrderListProps {
  openOrders: Order[];
  closedOrders: Order[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const OrderList: React.FC<OrderListProps> = ({ openOrders, closedOrders }) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const handleOpenModal = (orderId: string) => {
    setSelectedOrderId(orderId);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedOrderId(null);
  };

  const handleConfirmCancel = async () => {
    if (selectedOrderId) {
      await handleCancelOrder(selectedOrderId);
    }
    handleCloseModal();
  };

  const [cancelOrder, { isLoading }] = useCancelOrderMutation();

  const handleCancelOrder = async (orderId: string) => {
    try {
      const response = await cancelOrder({ orderId }).unwrap();
      console.log("Order cancelled successfully:", response);
    } catch (error) {
      console.error("Failed to cancel order:", error);
    }
  };

  const openOrdersColumns: GridColDef[] = [
    { field: "side", headerName: "Side", type: "string", width: 150 },
    { field: "symbol", headerName: "Symbol", type: "string", width: 150 },
    { field: "quantity", headerName: "Quantity", type: "number", width: 150 },
    { field: "limit", headerName: "Price", type: "number", width: 200 },
    {
      field: "initiationTime",
      headerName: "Created",
      type: "string",
      width: 250,
    },
    {
      field: "action",
      headerName: "Action",
      type: "string",
      width: 150,
      renderCell: (params) => (
        <LoadingButton
          color="error"
          onClick={() => handleOpenModal(params.row.orderId)}
          variant="contained"
        >
          Cancel
        </LoadingButton>
      ),
    },
  ];

  const allOrdersColumns: GridColDef[] = [
    { field: "side", headerName: "Side", type: "string", width: 150 },
    { field: "symbol", headerName: "Symbol", type: "string", width: 150 },
    { field: "status", headerName: "Status", type: "string", width: 150 },
    { field: "quantity", headerName: "Quantity", type: "number", width: 150 },
    { field: "limit", headerName: "Price", type: "number", width: 200 },
    {
      field: "initiationTime",
      headerName: "Created",
      type: "string",
      width: 250,
    },
  ];
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Card>
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="cancel-order-dialog-title"
        aria-describedby="cancel-order-dialog-description"
      >
        <DialogTitle id="cancel-order-dialog-title">Cancel Order</DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-order-dialog-description">
            Are you sure you want to cancel this order?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            No
          </Button>
          <LoadingButton
            onClick={handleConfirmCancel}
            color="error"
            loading={isLoading}
            variant="contained"
          >
            Yes, Cancel
          </LoadingButton>
        </DialogActions>
      </Dialog>
      ;
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="order list tabs"
        >
          <Tab label="Open Orders" {...a11yProps(0)} />
          <Tab label="All orders" {...a11yProps(1)} />
        </Tabs>
      </Box>
      <CustomTabPanel value={value} index={0}>
        <DataGrid
          rows={openOrders}
          hideFooterPagination={true}
          columns={openOrdersColumns}
          sx={{ height: "400px" }}
        />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <DataGrid
          rows={closedOrders}
          hideFooterPagination={true}
          columns={allOrdersColumns}
          sx={{ height: "400px" }}
        />
      </CustomTabPanel>
    </Card>
  );
};

export default OrderList;
